import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  TextField,
  Box,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { FormattedMessage, useIntl } from "react-intl";
import { PrimarySpinner } from "../../spinner/primary-spinner";
import React, { useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { loginValidators } from "../../../services/login-validators";
import { showErrorToast } from "../../../toast/toast-service";
import { errorConstants } from "../../../error-constants";
import { useMutation } from "@apollo/client";
import { LOGIN_USER } from "../../../apollo-client/mutations";
import { handlePressEnter } from "../../../helpers";

export const LoginFields = () => {
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();

  const [loginFormValues, setLoginFormValues] = useState({
    email: "",
    password: "",
  });
  const [loginFormValueErrors, setLoginFormValueErrors] = useState({
    email: false,
    password: false,
  });
  const [canLoginFormBeSent, setCanLoginFormBeSent] = useState(true);

  const [loginUser, { loading: loadingLoginUser }] = useMutation(LOGIN_USER);

  const intl = useIntl();

  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const resetLoginFormErrors = () => {
    setLoginFormValueErrors({
      email: false,
      password: false,
    });
  };
  const handleEmailInputChange = (event) => {
    const emailInput = event.target.value;

    setLoginFormValues({
      ...loginFormValues,
      email: emailInput,
    });
    resetLoginFormErrors();
    setCanLoginFormBeSent(true);
  };

  const handlePasswordInputChange = (event) => {
    const passportInputText = event.target.value;

    setLoginFormValues({
      ...loginFormValues,
      password: passportInputText,
    });
    resetLoginFormErrors();
    setCanLoginFormBeSent(true);
  };

  const normalLogin = (e) => {
    // must be set for Button.onSubmit method, because otherwise it refreshes the page without waiting for the mutation to succeed
    e.preventDefault();

    let emailFieldError = false;

    const areLoginInputFieldsValid = () => !emailFieldError;

    try {
      loginValidators.validateEmail(loginFormValues.email, intl);
    } catch (err) {
      emailFieldError = err.message;
      emailRef.current.focus();
    }

    setLoginFormValueErrors({
      email: emailFieldError,
    });

    setCanLoginFormBeSent(areLoginInputFieldsValid());

    if (areLoginInputFieldsValid()) {
      loginUser({
        variables: {
          email: loginFormValues.email,
          password: loginFormValues.password,
        },
      })
        .then(({ data }) => {
          localStorage.setItem("token", data.login.jwt);
          window.location.reload();
        })
        .catch((error) => {
          const graphQLErrorMessage = error.graphQLErrors[0]?.message;
          const errorMessage = intl.formatMessage(
            {
              id: "login.error",
            },
            { error: graphQLErrorMessage }
          );
          showErrorToast(errorMessage);

          if (graphQLErrorMessage === errorConstants.wrongPassword) {
            setLoginFormValueErrors({
              ...loginFormValueErrors,
              password: errorMessage,
            });
            passwordRef.current.focus();
          } else if (graphQLErrorMessage === errorConstants.emailNotFound) {
            setLoginFormValueErrors({
              ...loginFormValueErrors,
              email: errorMessage,
            });
            emailRef.current.focus();
          } else if (graphQLErrorMessage === "userIsNotVerified") {
            setLoginFormValueErrors({
              ...loginFormValueErrors,
              email: errorMessage,
            });
            emailRef.current.focus();
          }
        });
    }
  };
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Box sx={{ width: '100%', maxWidth: '320px' }}>
        <FormControl fullWidth>
          <Stack spacing={3}>
            <TextField
              label="E-mail"
              size="medium"
              variant="outlined"
              inputRef={emailRef}
              type="email"
              value={loginFormValues.email}
              error={!!loginFormValueErrors.email}
              helperText={loginFormValueErrors.email}
              onChange={handleEmailInputChange}
              onKeyDown={(event) =>
                handlePressEnter(event, () => normalLogin(event))
              }
              InputProps={{
                inputProps: {
                  "data-testid": "login-sidebar-email-input",
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '15px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    border: '2px solid rgba(232, 121, 117, 0.1)',
                    borderRadius: '12px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(232, 121, 117, 0.3)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#E87975',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 0 0 3px rgba(232, 121, 117, 0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                    borderWidth: '2px',
                  },
                  '&.Mui-error': {
                    backgroundColor: 'rgba(244, 67, 54, 0.05)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.grey[600],
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#E87975',
                  },
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '12px',
                  fontWeight: 500,
                },
              }}
            />
            
            <TextField
              label={intl.formatMessage({
                id: "general.placeholder-password",
              })}
              variant="outlined"
              size="medium"
              inputRef={passwordRef}
              value={loginFormValues.password}
              error={!!loginFormValueErrors.password}
              helperText={loginFormValueErrors.password}
              type={showPassword ? "text" : "password"}
              onChange={handlePasswordInputChange}
              onKeyDown={(event) =>
                handlePressEnter(event, () => normalLogin(event))
              }
              InputProps={{
                inputProps: {
                  "data-testid": "login-sidebar-password-input",
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      edge="end"
                      size="small"
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{
                        color: theme.palette.grey[600],
                        '&:hover': {
                          color: '#E87975',
                          backgroundColor: 'rgba(232, 121, 117, 0.1)',
                        },
                      }}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '15px',
                  borderRadius: '12px',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    border: '2px solid rgba(232, 121, 117, 0.1)',
                    borderRadius: '12px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(232, 121, 117, 0.3)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#E87975',
                    borderWidth: '2px',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0 0 0 3px rgba(232, 121, 117, 0.1)',
                  },
                  '&.Mui-error fieldset': {
                    borderColor: '#f44336',
                    borderWidth: '2px',
                  },
                  '&.Mui-error': {
                    backgroundColor: 'rgba(244, 67, 54, 0.05)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: theme.palette.grey[600],
                  fontWeight: 500,
                  '&.Mui-focused': {
                    color: '#E87975',
                  },
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '12px',
                  fontWeight: 500,
                },
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
              <Button
                component={Link}
                to="/forgot-password"
                variant="text"
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: theme.palette.grey[600],
                  padding: '6px 12px',
                  minWidth: 'auto',
                  borderRadius: '8px',
                  '&:hover': {
                    color: '#E87975',
                    backgroundColor: 'rgba(232, 121, 117, 0.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <FormattedMessage id="general.forgot-password-button" />
              </Button>
            </Box>

            <Button
              type="submit"
              disabled={!canLoginFormBeSent || loadingLoginUser}
              onClick={normalLogin}
              onSubmit={normalLogin}
              data-testid="login-sidebar-submit-button"
              variant="contained"
              sx={{
                textTransform: 'none',
                height: '52px',
                fontSize: '16px',
                fontWeight: 700,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #E87975 0%, #F86B6B 100%)',
                boxShadow: '0 4px 15px rgba(232, 121, 117, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #F86B6B 0%, #FF8181 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(232, 121, 117, 0.4)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  background: 'linear-gradient(135deg, #cccccc 0%, #e0e0e0 100%)',
                  boxShadow: 'none',
                  transform: 'none',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {loadingLoginUser ? (
                <PrimarySpinner />
              ) : (
                <FormattedMessage id="general.login-button" />
              )}
            </Button>
          </Stack>
        </FormControl>
      </Box>
    </Box>
  );
};

